import React from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { IconCircleCheckFilled } from '@tabler/icons-react';
import type { EntryComponent } from 'cms/entries/entries';
import type { ICompareFees } from 'declarations/contentful';

const RenderCompareFees = (entry: ICompareFees) => {
  const { fields } = entry;

  return (
    <Col as="section" className="items-center bg-od-bg-primary py-20 max-md:py-12">
      <Col className="w-full max-w-280 px-6">
        <Col className="gap-6">
          <Col className="gap-3">
            <Text as="p" className="text-label-sm font-medium text-od-content-accent-brand">
              {fields.eyebrow}
            </Text>
            <Text
              as="h2"
              className="text-subheader-lg text-od-content-primary max-md:text-subheader-md"
            >
              {fields.title}
            </Text>
            <Text
              as="p"
              className="max-w-145 text-body-lg leading-relaxed text-od-content-secondary"
            >
              {fields.description}
            </Text>
          </Col>

          <Col className="gap-8 md:flex-row">
            {/* Competitor fee stack */}
            <Col className="flex-1 gap-8 rounded-2xl bg-od-bg-secondary p-10 max-md:p-6">
              <Text as="h3" className="text-subheader-sm text-od-content-primary">
                {fields.competitorCardTitle}
              </Text>
              <Col className="gap-2">
                {fields.competitorFees.map((fee, idx) => (
                  <React.Fragment key={fee.sys.id}>
                    {/* border-red-100 matches the original $borderStateInactiveAccentError (#f8ebeb); no od-* token maps to this primitive */}
                    <Col className="gap-0.5 rounded-xl border border-red-100 bg-od-bg-error-secondary p-5">
                      <Text as="span" className="text-label-sm text-od-content-secondary">
                        {fee.fields.label}
                      </Text>
                      <Row className="items-baseline gap-2">
                        <Text
                          as="span"
                          className="text-subheader-xs font-medium text-od-content-error"
                        >
                          {fee.fields.value}
                        </Text>
                        {fee.fields.subValue && (
                          <Text
                            as="span"
                            className="text-body-sm font-medium text-od-content-secondary"
                          >
                            {fee.fields.subValue}
                          </Text>
                        )}
                      </Row>
                      <Text as="span" className="text-body-sm text-od-content-secondary">
                        {fee.fields.note}
                      </Text>
                    </Col>
                    {idx < fields.competitorFees.length - 1 && (
                      <Text
                        as="span"
                        className="py-0.5 text-center text-body-xl font-medium text-od-content-error"
                      >
                        +
                      </Text>
                    )}
                  </React.Fragment>
                ))}
              </Col>
              <Col className="gap-0.5 rounded-xl border-l-2 border-l-od-border-error bg-od-bg-error-secondary p-5">
                <Text as="span" className="text-label-sm text-od-content-secondary">
                  {fields.competitorTotalLabel}
                </Text>
                <Text as="span" className="text-body-xl font-medium text-od-content-error">
                  {fields.competitorTotalValue}
                </Text>
                {fields.competitorAttribution && (
                  <Text as="span" className="text-body-sm text-od-content-tertiary">
                    {fields.competitorAttribution}
                  </Text>
                )}
              </Col>
            </Col>

            {/* Opendoor simplicity */}
            <Col className="flex-1 gap-8 rounded-2xl border-2 border-od-border-accent-brand bg-od-bg-primary p-10 max-md:p-6">
              <Text as="h3" className="text-subheader-sm text-od-content-primary">
                {fields.opendoorCardTitle}
              </Text>
              <Col className="gap-2">
                <Col className="gap-0.5 rounded-xl border border-od-border-secondary bg-od-bg-secondary p-5">
                  <Text as="span" className="text-label-sm text-od-content-secondary">
                    {fields.opendoorFeeLabel}
                  </Text>
                  <Text as="span" className="text-subheader-xs font-medium text-od-content-success">
                    {fields.opendoorFeeValue}
                  </Text>
                  <Text as="span" className="text-body-sm text-od-content-secondary">
                    {fields.opendoorFeeNote}
                  </Text>
                </Col>
              </Col>
              <Col className="gap-4">
                {fields.opendoorBenefits.map((benefit, idx) => (
                  <Row key={idx} className="items-center gap-3">
                    <IconCircleCheckFilled
                      size={20}
                      className="text-od-content-success"
                      aria-hidden="true"
                    />
                    <Text as="span" className="text-body-md font-medium text-od-content-primary">
                      {benefit}
                    </Text>
                  </Row>
                ))}
              </Col>
              <Col className="gap-0.5 rounded-xl border-l-2 border-l-od-border-accent-brand bg-od-bg-secondary p-5">
                <Text as="span" className="text-label-sm text-od-content-secondary">
                  {fields.opendoorBottomLineLabel}
                </Text>
                <Text as="span" className="text-body-xl font-medium text-od-content-accent-brand">
                  {fields.opendoorBottomLineValue}
                </Text>
              </Col>
            </Col>
          </Col>
        </Col>
      </Col>
    </Col>
  );
};

const CompareFees: EntryComponent<ICompareFees> = {
  render: RenderCompareFees,
};

export default CompareFees;
